/**
 * `BriefDetailHeader` — header for the editable brief detail page
 * (MFINS-2519 + MFINS-2520).
 *
 * Save enable rule: `disabled = !isValid || (!isDirty && !isSaving)`.
 * Invalid blocks always (R5 — empty title); when valid, enabled if there
 * is something to save OR a save is currently in flight (so the user
 * sees the spinner without losing the affordance).
 *
 * "Mark as ready" button (MFINS-2520; relabeled in MFINS-3296 — was
 * "Submit for approval"): owner page derives a single `submitDisabledReason`.
 * When the reason is non-null the button uses `aria-disabled` (NOT the
 * native `disabled` attribute) so the Radix Tooltip trigger keeps receiving
 * pointer/focus events — same pattern as
 * `entities/project-comment/ui/Comment.tsx`. The button never shows in-flight
 * state: it opens the confirmation dialog, and the transition it starts runs
 * and reports itself in there.
 *
 * Ready terminal state (post-MFINS-3296 polish): once `brief.status` flips
 * to "submitted" (rendered as "Ready") the action button is no longer
 * actionable — but the previous "Mark as ready" label + tooltip on hover
 * read as misleading copy. The button now collapses to a self-explanatory
 * "Ready" + Check pill, disabled, with NO tooltip (the label is the state).
 */
import type { BriefResponseDto } from '@inference.engine/workspace-api-client/web';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import {
  Check,
  ClipboardCopy,
  Copy,
  Download,
  EllipsisVerticalIcon,
  InfoIcon,
  MessageSquareIcon,
  Pencil,
  RefreshCw,
} from 'lucide-react';
import { generatePath, useNavigate } from 'react-router';

import {
  PageHeader,
  PageHeaderActions,
  PageHeaderBackButton,
  PageHeaderBreadcrumb,
  PageHeaderBreadcrumbLink,
  PageHeaderBreadcrumbPage,
  PageHeaderIdentity,
} from '@/shared/ui/layout';
import { ROUTES } from '@/shared/config/routes';

import { BriefSaveIndicator, type SaveState } from '../BriefSaveIndicator';
import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import { DetailSidebarViewToggle, ScopedPermission } from '@/shared/ui';
import { useTranslation } from 'react-i18next';

interface BriefDetailHeaderProps {
  brief: BriefResponseDto;
  platformProjectId: string;
  onSave: () => void;
  saveState: SaveState;
  savedAt?: Date | null;
  saveError?: string | null;
  sidebar: { view: 'comments' | 'info' | undefined; toggle: (view: 'comments' | 'info') => void };
  onRetrySave?: () => void;
  isDirty: boolean;
  isValid: boolean;
  isSaving: boolean;
  onSubmit: () => void;
  submitDisabledReason: string | null;
  onEdit: () => void;
  onDuplicate: () => void;
  isDuplicating: boolean;
  onDownloadPdf: () => void;
  isExportingPdf: boolean;
  onDownloadMarkdown: () => void;
  onReconcileTemplate: () => void;
  isReconciling: boolean;
}

const STATUS_BADGE_VARIANT: Record<BriefResponseDto['status'], 'secondary' | 'positive'> = {
  draft: 'secondary',
  submitted: 'positive',
};

// The BE enum is intentionally NOT renamed (that would cascade through migrations, DTOs
// and the generated http-clients); `submitted` is relabelled to "Ready" only where rendered.
const STATUS_LABEL_KEY = {
  draft: 'projectDetail.briefsConstants.draft',
  submitted: 'projectDetail.briefsConstants.ready',
} as const satisfies Record<BriefResponseDto['status'], string>;

export function BriefDetailHeader({
  brief,
  platformProjectId,
  onSave,
  saveState,
  savedAt,
  saveError,
  onRetrySave,
  isDirty,
  isValid,
  isSaving,
  onSubmit,
  submitDisabledReason,
  onEdit,
  onDuplicate,
  isDuplicating,
  onDownloadPdf,
  isExportingPdf,
  onDownloadMarkdown,
  onReconcileTemplate,
  isReconciling,
  sidebar,
}: BriefDetailHeaderProps) {
  const { t } = useTranslation('projects');

  const navigate = useNavigate();
  const briefingPath = generatePath(ROUTES.PROJECT_BRIEFING(platformProjectId));

  const handleBack = () => {
    void navigate(briefingPath);
  };

  const saveDisabled = !isValid || (!isDirty && !isSaving);
  const isReady = brief.status === 'submitted';
  const submitBlocked = isReady || submitDisabledReason !== null;
  // Suppress the tooltip when the brief is already Ready — the button's
  // own label is the state, repeating "Already marked as ready" is noise.
  const showSubmitTooltip = !isReady && submitDisabledReason !== null;

  const submitButton = (
    <Button
      variant="default"
      size="lg"
      aria-disabled={submitBlocked || undefined}
      // aria-disabled (not the native `disabled`) so Radix Tooltip still
      // receives pointer/focus events. Click is suppressed when blocked.
      // The aria-disabled: variants paint the disabled look the native
      // attribute would have given us for free.
      className="aria-disabled:pointer-events-auto aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
      onClick={submitBlocked ? undefined : onSubmit}
      data-testid="brief-detail-submit-button"
    >
      {isReady ? (
        <>
          <Check className="size-3.5" aria-hidden />
          <span>{t('projectBriefDetail.briefDetailHeader.ready')}</span>
        </>
      ) : (
        t('projectBriefDetail.briefDetailHeader.markAsReady')
      )}
    </Button>
  );

  return (
    <PageHeader focus>
      <PageHeaderIdentity>
        <PageHeaderBackButton onClick={handleBack} />
        <PageHeaderBreadcrumb>
          <PageHeaderBreadcrumbLink href={briefingPath}>
            {t('projectBriefDetail.briefDetailHeader.briefs')}
          </PageHeaderBreadcrumbLink>
          <PageHeaderBreadcrumbPage>{brief.title}</PageHeaderBreadcrumbPage>
        </PageHeaderBreadcrumb>
        <Badge
          data-testid="brief-detail-status-badge"
          variant={STATUS_BADGE_VARIANT[brief.status]}
          roundness="full"
        >
          {t(STATUS_LABEL_KEY[brief.status])}
        </Badge>
      </PageHeaderIdentity>
      <PageHeaderActions>
        <BriefSaveIndicator
          state={saveState}
          savedAt={savedAt}
          error={saveError}
          onRetry={onRetrySave}
        />
        <DetailSidebarViewToggle
          icon={<MessageSquareIcon className="size-4" />}
          view="comments"
          openView={sidebar.view}
          onToggle={sidebar.toggle}
          hideLabel={t('projectBriefDetail.briefDetailHeader.hideCommentsPanel')}
          showLabel={t('projectBriefDetail.briefDetailHeader.showCommentsPanel')}
          dataTestId="brief-sidebar-toggle-comments"
        />
        <DetailSidebarViewToggle
          icon={<InfoIcon className="size-4" />}
          view="info"
          openView={sidebar.view}
          onToggle={sidebar.toggle}
          hideLabel={t('projectBriefDetail.briefDetailHeader.hideInfoPanel')}
          showLabel={t('projectBriefDetail.briefDetailHeader.showInfoPanel')}
          dataTestId="brief-sidebar-toggle-info"
        />
        <ScopedPermission permissions={[PROJECT_PERMISSIONS.ProjectEditDetails]}>
          {showSubmitTooltip ? (
            <Tooltip delayDuration={150}>
              <TooltipTrigger asChild>{submitButton}</TooltipTrigger>
              <TooltipContent>{submitDisabledReason}</TooltipContent>
            </Tooltip>
          ) : (
            submitButton
          )}
        </ScopedPermission>
        <Button
          variant="outline"
          size="lg"
          disabled={saveDisabled}
          onClick={onSave}
          aria-label={t('projectBriefDetail.briefDetailHeader.save')}
        >
          {isSaving ? (
            <>
              <Spinner className="size-3.5" aria-hidden />
              <span>{t('projectBriefDetail.briefDetailHeader.saving')}</span>
            </>
          ) : (
            t('projectBriefDetail.briefDetailHeader.save')
          )}
        </Button>
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-lg"
                  aria-label={t('projectBriefDetail.briefDetailHeader.moreActions')}
                >
                  <EllipsisVerticalIcon className="size-4" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {t('projectBriefDetail.briefDetailHeader.moreActions')}
            </TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="end">
            <ScopedPermission
              permissions={[
                PROJECT_PERMISSIONS.ProjectUploadAssets,
                PROJECT_PERMISSIONS.ProjectEditDetails,
              ]}
            >
              <DropdownMenuItem onClick={onEdit}>
                <Pencil className="mr-2 size-4" />
                {t('projectBriefDetail.briefDetailHeader.edit')}
              </DropdownMenuItem>
            </ScopedPermission>
            <ScopedPermission permissions={[PROJECT_PERMISSIONS.ProjectEditDetails]}>
              <DropdownMenuItem onClick={onDuplicate} disabled={isDuplicating}>
                {isDuplicating ? (
                  <Spinner className="mr-2" aria-hidden />
                ) : (
                  <Copy className="mr-2 size-4" />
                )}
                {isDuplicating
                  ? t('projectBriefDetail.briefDetailHeader.duplicating')
                  : t('projectBriefDetail.briefDetailHeader.duplicate')}
              </DropdownMenuItem>
            </ScopedPermission>
            <DropdownMenuItem onClick={onDownloadPdf} disabled={isExportingPdf}>
              {isExportingPdf ? (
                <Spinner className="mr-2" aria-hidden />
              ) : (
                <Download className="mr-2 size-4" />
              )}
              {isExportingPdf
                ? t('projectBriefDetail.briefDetailHeader.preparingPdf')
                : t('projectBriefDetail.briefDetailHeader.downloadPdf')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDownloadMarkdown}>
              <ClipboardCopy className="mr-2 size-4" />
              {t('projectBriefDetail.briefDetailHeader.downloadMarkdown')}
            </DropdownMenuItem>
            <ScopedPermission permissions={[PROJECT_PERMISSIONS.ProjectEditDetails]}>
              {/* Radix stops `onSelect` on a disabled item but leaves the DOM click handler
                  attached, so the guard has to be on the handler as well. */}
              <DropdownMenuItem
                onClick={isReconciling ? undefined : onReconcileTemplate}
                disabled={isReconciling}
              >
                {isReconciling ? (
                  <Spinner className="mr-2" aria-hidden />
                ) : (
                  <RefreshCw className="mr-2 size-4" />
                )}
                {isReconciling
                  ? t('projectBriefDetail.briefDetailHeader.checkingForTemplateUpdates')
                  : t('projectBriefDetail.briefDetailHeader.checkForTemplateUpdates')}
              </DropdownMenuItem>
            </ScopedPermission>
          </DropdownMenuContent>
        </DropdownMenu>
      </PageHeaderActions>
    </PageHeader>
  );
}
